/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import Loader from "../../components/Loader/Loader.js";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { esES } from "@mui/material/locale";
import { PlusOutlined, FilterOutlined } from "@ant-design/icons";
import {
  Alert,
  Dropdown,
  Modal as ModalAntd,
  Popover,
  notification,
} from "antd";
import axios from "axios";
import { BACKEND_URL, FILES_URL, apiBaseUrl, env } from "../../config/config";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import { ExportPdf, ExportCsv } from "@material-table/exporters";
import { Button } from "antd";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import Modal from "../../components/Modal/Modal";
import {
  AccountDocumentUploadModal,
  BuyCreditsModal,
  OpenModalAccount,
  OpenModalPCI,
} from "../../components/Account";
import moment from "moment";
import { useAuth } from "../../hooks";

const resolveFileUrl = (filePath) => {
  const value = String(filePath || "").trim();

  if (!value) {
    return "";
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const normalizedPath = value.replace(/^\/+/, "");

  if (/^uploaded\//i.test(normalizedPath)) {
    return `${apiBaseUrl}/${normalizedPath}`;
  }

  if (/^files\//i.test(normalizedPath)) {
    return `${apiBaseUrl}/uploaded/${normalizedPath}`;
  }

  return `${FILES_URL}/${normalizedPath}`;
};

// Mirrors parc360SelfInspectionBaseUrlByHost in the CRM's
// jquery.parcControlPanel.js, keyed by this app's own env instead of
// sniffing window.location.hostname.
const SELF_INSPECTION_BASE_URL_BY_ENV = {
  local: "http://localhost:3000",
  testing: "https://testing.selfinspection.potholerepair.com",
  production: "https://selfinspection.potholerepair.com",
};

export function Accounts({ dataChart, pageSize, title }) {
  const [openMySitesData, setMySitesData] = useState([]);
  const [filtering, setFiltering] = React.useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState(null);
  const [modalSize, setModalSize] = useState("md");
  const [uploadAccount, setUploadAccount] = useState(null);
  const [buyCreditsAccount, setBuyCreditsAccount] = useState(null);
  const [creditPools, setCreditPools] = useState([]);
  const [creditPoolsStatus, setCreditPoolsStatus] = useState("loading");
  const [loadError, setLoadError] = useState(false);
  const [openParcMenuId, setOpenParcMenuId] = useState(null);
  const [selfInspectionLinks, setSelfInspectionLinks] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showArrow, setShowArrow] = useState(true);
  const [arrowAtCenter, setArrowAtCenter] = useState(false);

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { user } = useAuth();
  const { confirm } = ModalAntd;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getData = async () => {
    setIsLoading(true);
    setLoadError(false);
    const url = `${BACKEND_URL}/customers/getDashboard?id=` + user?._id;

    const dataDashboard = await axios.get(url).catch((err) => {
      console.log(err);
      return null;
    });

    if (dataDashboard?.data) {
      const payload = dataDashboard?.data?.data;
      setMySitesData(payload.mySites);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      setLoadError(true);
      console.log("No dashboard data retrieved");
    }
  };

  const getCreditPools = async () => {
    setCreditPoolsStatus("loading");
    const url = `${BACKEND_URL}/customers/getPaymentCreditBalance?id=` + user?._id;

    const response = await axios.get(url).catch((err) => {
      console.log(err);
      return null;
    });

    if (response?.data?.creditPools) {
      setCreditPools(response.data.creditPools);
      setCreditPoolsStatus("loaded");
    } else {
      setCreditPoolsStatus("error");
    }
  };

  useEffect(() => {
    if (user) {
      getData();
      getCreditPools();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const getCreditPoolForAccount = (accountId) =>
    creditPools.find((pool) =>
      (pool.sites || []).some((site) => String(site._id) === String(accountId))
    );

  const formatCents = (cents) =>
    (Number(cents || 0) / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });

  const hasActiveParcEndDate = (rd) =>
    !!rd?.PARCAgreementEndDate &&
    new Date(rd.PARCAgreementEndDate) >= today;

  // A site is only "PARC 360 enabled" once a real plan (PARCAgreementType)
  // has been assigned to it - the generic PARCAgreement+dates flag gets
  // written onto every account in a hierarchy (portfolio/grouping accounts
  // and the purchaser root included), so it can't be used on its own to
  // decide this. Shared by the Contracted Plan column and the PARC 360
  // options menu so both agree on what "enrolled" means.
  const hasRealPlan = (rd) =>
    !!rd?.PARCAgreementType?.name &&
    hasActiveParcEndDate(rd);

  // PCI is part of an active PARC service. A populated PARCAgreementType
  // identifies PARC 360; the legacy PARCAgreement flag identifies standard
  // PARC. The date check is deliberately shared so expired coverage cannot
  // expose PCI through either path.
  const hasPciAccess = (rd) =>
    hasRealPlan(rd) ||
    (rd?.PARCAgreement === true && hasActiveParcEndDate(rd));

  const isParcEnrollmentPending = (rd) =>
    !hasRealPlan(rd) &&
    rd?.PARCAgreement === true &&
    hasActiveParcEndDate(rd);

  // Attaches row-level fields MaterialTable's field-based sort/search/filter
  // need to be real primitives instead of nested objects or cross-fetched
  // data: a plain string for Main Contact (the raw field is an object) and
  // the resolved credits balance/status for each site (credits are fetched
  // separately from the site list and joined here, once, for every column
  // and for export - instead of each column re-deriving it independently).
  const withComputedFields = (rows) =>
    (rows || []).map((row) => {
      const pool = getCreditPoolForAccount(row._id);
      const creditsPoolStatus = pool
        ? "enrolled"
        : creditPoolsStatus === "error"
        ? "unavailable"
        : creditPoolsStatus === "loading"
        ? "loading"
        : "not-enrolled";

      return {
        ...row,
        mainContactName: getMainContactName(row),
        creditsBalanceCents: pool ? pool.currentBalanceCents : null,
        creditsPoolStatus,
      };
    });

  const theme = createTheme(
    {
      palette: { primary: { main: "#2563eb" } },
    },
    esES
  );

  const createAction = () => {};

  const contentPopover = (data) => {
    return (
      <div>
        <p>
          <span style={{ fontWeight: "bold" }}>Start Date: </span>
          {moment(data?.PARCAgreementStartDate).format("MM/DD/YYYY")},{" "}
          <span style={{ fontWeight: "bold" }}>End Date: </span>{" "}
          {moment(data?.PARCAgreementEndDate).format("MM/DD/YYYY")}
        </p>
      </div>
    );
  };

  const text = <span>PARC</span>;

  const mergedArrow = useMemo(() => {
    if (arrowAtCenter) return { pointAtCenter: true };
    return showArrow;
  }, [showArrow, arrowAtCenter]);

  // ========= Helpers for UI and Export =========
  const getSiteName = (row) =>
    (row?.accountName || row?.companyName || "").toString();

  const getMainContactName = (row) =>
    (row?.mainContact?.fullName || row?.mainContact || "").toString();

  const getCityName = (row) => (row?.city?.name || row?.city || "").toString();

  const getStateName = (row) =>
    (row?.state?.name || row?.state || "").toString();

  const yesNo = (v) => (v ? "Yes" : "No");

  const getPciHistory = (row) =>
    Array.isArray(row?.PCIHistory) ? row.PCIHistory : [];

  const getLatestPciHistory = (row) => {
    const history = getPciHistory(row);
    return history.length ? history[history.length - 1] : null;
  };

  const getPciScore = (row) => {
    const latestHistory = getLatestPciHistory(row);
    const rawScore =
      row?.PCIValue !== null && typeof row?.PCIValue !== "undefined"
        ? row.PCIValue
        : latestHistory?.finalScore;
    const score = Number(rawScore);

    if (!Number.isFinite(score) || score < 0) return null;
    return Math.round(score);
  };

  const getPciStatus = (row) => String(row?.PCIStatus || "").toLowerCase();

  const hasPciDetail = (row) => {
    const status = getPciStatus(row);
    return (
      getPciScore(row) !== null ||
      getPciHistory(row).length > 0 ||
      (status && status !== "toanalyse")
    );
  };

  const isPciActive = (row) => {
    const expirationDate = row?.PCIValueExpirationDate
      ? new Date(row.PCIValueExpirationDate)
      : null;

    return (
      getPciScore(row) !== null &&
      (!expirationDate || expirationDate >= today) &&
      ["finished", "done", "completed"].includes(getPciStatus(row))
    );
  };

  const isPciExpired = (row) => {
    const expirationDate = row?.PCIValueExpirationDate
      ? new Date(row.PCIValueExpirationDate)
      : null;

    return !!expirationDate && expirationDate < today;
  };

  const getPciChipClass = (row) => {
    const score = getPciScore(row);
    const status = getPciStatus(row);

    if (score !== null) {
      if (score <= 30) return "pci-score-chip--danger";
      if (score <= 69) return "pci-score-chip--warning";
      return "pci-score-chip--success";
    }

    if (status === "inprogress" || status === "pending")
      return "pci-score-chip--warning";
    return "pci-score-chip--muted";
  };

  const getPciChipLabel = (row) => {
    const status = getPciStatus(row);

    if (status === "inprogress" || status === "pending") return "Processing";
    if (isPciExpired(row)) return "Expired";
    if (getPciScore(row) !== null) return "Active";
    return "No PCI";
  };

  const formatPciDate = (date) => {
    if (!date) return "-";
    const parsedDate = moment(date);
    return parsedDate.isValid() ? parsedDate.format("MM/DD/YYYY") : "-";
  };

  const renderSite = (rowData) => {
    const isLead = !!rowData?.companyName;
    const Icon = isLead ? PersonAddAltOutlinedIcon : BusinessOutlinedIcon;
    const coverPicUrl = resolveFileUrl(rowData?.coverPic);

    return (
      <span className="table-entity">
        <span className="table-entity__icon">
          {coverPicUrl ? (
            <img
              src={coverPicUrl}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
            />
          ) : (
            <Icon />
          )}
        </span>
        <span className="table-entity__text" title={getSiteName(rowData)}>
          {getSiteName(rowData)}
        </span>
      </span>
    );
  };

  const renderYesNoPill = (active, labelActive, labelInactive) => (
    <span
      className={`table-status-pill ${
        active ? "table-status-pill--success" : "table-status-pill--muted"
      }`}
    >
      {active ? (
        <CheckCircleOutlineIcon className="table-status-pill__icon" />
      ) : (
        <CancelOutlinedIcon className="table-status-pill__icon" />
      )}
      {active ? labelActive : labelInactive}
    </span>
  );

  const renderPciPopoverContent = (rowData) => {
    const latestHistory = getLatestPciHistory(rowData);
    const score = getPciScore(rowData);

    return (
      <div className="pci-popover-card">
        <div className="pci-popover-card__row">
          <span>PCI Number</span>
          <strong>{score === null ? "--" : score}</strong>
        </div>
        <div className="pci-popover-card__row">
          <span>Status</span>
          <strong>{getPciChipLabel(rowData)}</strong>
        </div>
        <div className="pci-popover-card__row">
          <span>Last PCI</span>
          <strong>{formatPciDate(latestHistory?.dateRequested)}</strong>
        </div>
        <div className="pci-popover-card__row">
          <span>Next PCI</span>
          <strong>{formatPciDate(rowData?.PCIValueExpirationDate)}</strong>
        </div>
        <div className="pci-popover-card__hint">
          Click the PCI badge to view images and full detail.
        </div>
      </div>
    );
  };

  const renderPciChip = (rowData) => {
    const hasAccess = hasPciAccess(rowData);
    const hasDetail = hasPciDetail(rowData);
    const score = getPciScore(rowData);

    // PCI data/score are reserved for sites with an active PARC or PARC 360
    // agreement - the chip stays visible and clickable either way, but never
    // reveals score/status until access is confirmed. Locked click opens a
    // "reserved" screen instead of the real PCI detail (see openPciDetail).
    const chip = hasAccess ? (
      <button
        type="button"
        className={`pci-score-chip ${getPciChipClass(rowData)} pci-score-chip--clickable`}
        onClick={(event) => {
          event.stopPropagation();
          openPciDetail(rowData);
        }}
      >
        <AssessmentOutlinedIcon className="pci-score-chip__icon" />
        <span className="pci-score-chip__score">
          {score === null ? "--" : score}
        </span>
        <span className="pci-score-chip__label">
          {getPciChipLabel(rowData)}
        </span>
      </button>
    ) : (
      <button
        type="button"
        className="pci-score-chip pci-score-chip--muted pci-score-chip--clickable"
        onClick={(event) => {
          event.stopPropagation();
          openPciDetail(rowData);
        }}
      >
        <LockOutlinedIcon className="pci-score-chip__icon" />
        <span className="pci-score-chip__label">Reserved</span>
      </button>
    );

    if (!hasAccess || !hasDetail) {
      return chip;
    }

    return (
      <Popover
        placement="bottom"
        title="PCI Summary"
        content={renderPciPopoverContent(rowData)}
        arrow={mergedArrow}
      >
        {chip}
      </Popover>
    );
  };

  const renderInvoicesPill = (hasOpen) => (
    <span
      className={`table-status-pill ${
        hasOpen ? "table-status-pill--warning" : "table-status-pill--success"
      }`}
    >
      {hasOpen ? (
        <ErrorOutlineOutlinedIcon className="table-status-pill__icon" />
      ) : (
        <CheckCircleOutlineIcon className="table-status-pill__icon" />
      )}
      {hasOpen ? "Open" : "None"}
    </span>
  );

  const renderContractedPlan = (rowData) => {
    if (hasRealPlan(rowData)) {
      return (
        <Popover
          placement="bottom"
          title={text}
          content={contentPopover(rowData)}
          arrow={mergedArrow}
        >
          <span className="table-status-pill">
            <HandshakeOutlinedIcon className="table-status-pill__icon" />
            {rowData.PARCAgreementType.name}
          </span>
        </Popover>
      );
    }

    if (isParcEnrollmentPending(rowData)) {
      return (
        <span className="table-status-pill table-status-pill--warning">
          <AutorenewOutlinedIcon className="table-status-pill__icon" />
          Enrollment pending
        </span>
      );
    }

    return (
      <span className="table-status-pill table-status-pill--muted">
        <CancelOutlinedIcon className="table-status-pill__icon" />
        Not enrolled
      </span>
    );
  };

  const renderCreditsCell = (rowData) => {
    if (rowData.creditsPoolStatus === "loading") {
      return <span className="table-number-cell">--</span>;
    }

    if (rowData.creditsPoolStatus === "unavailable") {
      return (
        <span className="table-status-pill table-status-pill--warning">
          <ErrorOutlineOutlinedIcon className="table-status-pill__icon" />
          Unavailable
        </span>
      );
    }

    if (rowData.creditsPoolStatus !== "enrolled") {
      return (
        <span className="table-status-pill table-status-pill--muted">
          Not enrolled
        </span>
      );
    }

    return (
      <span className="table-number-cell">
        {formatCents(rowData.creditsBalanceCents)}
      </span>
    );
  };

  // ========= Columns (UI) =========
  const columns = [
    {
      title: "Site",
      field: "accountName",
      render: renderSite,
      width: "20%",
    },
    {
      title: "Main Contact",
      field: "mainContactName",
      render: (rowData) => <span>{getMainContactName(rowData)}</span>,
      width: "14%",
    },
    {
      title: "Location",
      field: "city.name",
      render: (r) => {
        const city = getCityName(r);
        const state = getStateName(r);
        return <span>{[city, state].filter(Boolean).join(", ") || "--"}</span>;
      },
      width: "14%",
    },
    {
      title: "Open Quotes",
      field: "hasQuotes",
      render: (rowData) => renderYesNoPill(rowData.hasQuotes === true, "Open", "None"),
      width: "9%",
    },
    {
      title: "PCI",
      field: "PCIValue",
      render: renderPciChip,
      customSort: (a, b) => (getPciScore(a) ?? -1) - (getPciScore(b) ?? -1),
      width: "10%",
    },
    {
      title: "Contracted Plan",
      field: "PARCAgreementType.name",
      render: renderContractedPlan,
      width: "13%",
    },
    {
      title: "PARC 360 Credits",
      field: "creditsBalanceCents",
      render: renderCreditsCell,
      customSort: (a, b) => (a.creditsBalanceCents ?? -1) - (b.creditsBalanceCents ?? -1),
      cellStyle: { textAlign: "right" },
      headerStyle: { textAlign: "right" },
      width: "11%",
    },
    {
      title: "Open Invoices",
      field: "hasOpenInvoices",
      render: (rowData) => renderInvoicesPill(rowData.hasOpenInvoices === true),
      width: "9%",
    },
  ];

  const onCreateButtonClick = () => {};

  const onView = (data) => {
    setIsVisibleModal(true);
    setModalSize("md");
    setModalTitle("Account information");
    setModalContent(<OpenModalAccount data={data} />);
  };

  const openUploadDocument = (data) => {
    setUploadAccount(data);
  };

  const openPciDetail = async (data) => {
    setIsVisibleModal(true);
    setModalSize("xl");
    setModalTitle(`PCI - ${getSiteName(data)}`);

    if (!hasPciAccess(data)) {
      setModalContent(<OpenModalPCI site={data} locked />);
      return;
    }

    const origin = data?.origin || (data?.companyName ? "lead" : "account");

    setModalContent(<OpenModalPCI site={data} isLoading />);

    const url = `${BACKEND_URL}/pci/entities/${origin}/${data?._id}/processes`;
    const response = await axios.get(url).catch((err) => {
      console.log("error ", err);
      setModalContent(
        <OpenModalPCI
          site={data}
          error="Unable to load PCI information"
        />
      );
      return null;
    });

    if (response?.data) {
      setModalContent(<OpenModalPCI site={data} data={response.data} />);
    }
  };

  const showPciAccessMessage = () => {
    notification.warning({
      message: "PCI access requires an active PARC agreement",
      description:
        "PCI information and inspections are available only with an active PARC or PARC 360 agreement.",
    });
  };

  const requestParcRenewal = (data) => {
    confirm({
      title: "Renew PARC",
      content: `By clicking “yes” below, you are confirming authorization to renew coverage for one additional year for "${getSiteName(
        data
      )}". You will receive updated documentation on the agreement and be invoiced electronically`,
      okText: "Yes",
      okType: "primary",
      cancelText: "No",
      onOk: async () => {
        setIsLoading(true);

        const dataSend = {
          contact: getMainContactName(data),
          accountName: getSiteName(data),
          account_id: data._id,
          owner: data.owner,
          origin: data.accountName ? "account" : "lead",
          contact_id: user?._id,
          contacID: data?.mainContact?._id,
        };

        const url = `${BACKEND_URL}/customers/requestPARCRenewal`;
        const response = await axios.post(url, dataSend).catch((err) => {
          setIsLoading(false);
          console.log("error ", err);
          notification.error({
            message: "Renewal request failed",
            description: "We couldn't submit your renewal request. Please try again.",
          });
          return null;
        });

        if (response?.data) {
          setIsLoading(false);
          notification["success"]({ message: "Renewal requested successfully" });
        }
      },
    });
  };

  // Self-inspection has no real submit endpoint yet - the only real artifact
  // today is the Opportunity parc360process.js creates on the CRM side
  // (opportunityType PARC360, hash + status). Same lookup + link shape as
  // the CRM's own PARC 360 control panel tab (loadParc360SelfInspectionLink
  // in jquery.parcControlPanel.js), just resolved once per site and cached
  // instead of refetched on every menu open.
  const fetchSelfInspectionLink = async (siteId) => {
    setSelfInspectionLinks((prev) => ({
      ...prev,
      [siteId]: { status: "loading" },
    }));

    const response = await axios
      .get(`${BACKEND_URL}/getLastOpportunity`, {
        params: { account: siteId, opportunityType: "PARC360" },
      })
      .catch(() => null);

    const opportunity = response?.data;

    if (opportunity && opportunity.hash) {
      const baseUrl =
        SELF_INSPECTION_BASE_URL_BY_ENV[env] ||
        SELF_INSPECTION_BASE_URL_BY_ENV.production;
      setSelfInspectionLinks((prev) => ({
        ...prev,
        [siteId]: {
          status: "ready",
          url: `${baseUrl}/inspection/${opportunity.hash}`,
          opportunityStatus: opportunity.status,
        },
      }));
    } else if (response) {
      setSelfInspectionLinks((prev) => ({
        ...prev,
        [siteId]: { status: "unavailable", reason: "No self-inspection generated yet" },
      }));
    } else {
      setSelfInspectionLinks((prev) => ({
        ...prev,
        [siteId]: { status: "unavailable", reason: "Self-inspection status unavailable" },
      }));
    }
  };

  // "Do PCI" (photo capture + AI scoring) is a separate flow from viewing
  // one already on file (that's the PCI chip's own modal, unchanged) - same
  // pothole-parc360-selfinspection app as Self Inspection, /pci/[token]
  // route, gated by Account.token (see pci-token-gateway.server.controller.js)
  // rather than an Opportunity.hash, so unlike Self Inspection this is
  // already on the row data and needs no extra fetch. Mirrors the "Do PCI"
  // link in the CRM's own PARC 360 control panel tab.
  const openPciInspection = (rd) => {
    if (!hasPciAccess(rd)) {
      showPciAccessMessage();
      return;
    }

    if (!rd?.token) {
      notification.warning({
        message: "PCI inspection is not available yet",
        description: "No public PCI inspection link has been generated for this account.",
      });
      return;
    }

    const baseUrl =
      SELF_INSPECTION_BASE_URL_BY_ENV[env] ||
      SELF_INSPECTION_BASE_URL_BY_ENV.production;
    window.open(`${baseUrl}/pci/${rd.token}`, "_blank", "noopener,noreferrer");
  };

  const renderSelfInspectionMenuLabel = (rd) => {
    const link = selfInspectionLinks[rd._id];

    if (link?.status === "ready") {
      return (
        <a href={link.url} target="_blank" rel="noopener noreferrer">
          Self Inspection
        </a>
      );
    }

    if (link?.status === "unavailable") {
      return <span title={link.reason}>Self Inspection</span>;
    }

    return <span>Self Inspection…</span>;
  };

  const getSiteOptionsMenuItems = (rd) => {
    // Credits pool lives on the management (parent) account only, and only
    // once a CSR has actually enrolled it in Payment Credits Configuration -
    // a covered child site resolves its pool from an ancestor, it doesn't own
    // one, so buying credits is only offered here.
    const isPurchaserAccount = !rd?.parentAccount;
    const hasCreditPool = !!getCreditPoolForAccount(rd?._id);

    const items = [];

    if (hasRealPlan(rd)) {
      items.push({
        key: "renew",
        label: "Renew PARC",
        icon: <AutorenewOutlinedIcon fontSize="small" />,
        onClick: () => requestParcRenewal(rd),
      });

      items.push({
        key: "self-inspection",
        label: renderSelfInspectionMenuLabel(rd),
        icon: <QrCode2OutlinedIcon fontSize="small" />,
        disabled: selfInspectionLinks[rd._id]?.status !== "ready",
      });

    }

    items.push({
      key: "pci",
      label: "PCI Inspection",
      icon: <AssessmentOutlinedIcon fontSize="small" />,
      onClick: () => openPciInspection(rd),
    });

    if (isPurchaserAccount && hasCreditPool) {
      items.push({
        key: "credits",
        label: "Buy Credits",
        icon: <AccountBalanceWalletOutlinedIcon fontSize="small" />,
        onClick: () => setBuyCreditsAccount(rd),
      });
    }

    return items;
  };

  const requestSurvey = (data) => {
    confirm({
      title: "Request Survey",
      content: `By clicking “yes” below, you are confirming authorization to have our inspector survey and document the condition of the parking lot(s) at "${getSiteName(
        data
      )}". If you have any special requests or wish to meet our inspector onsite, please email info@potholerepair.com`,
      okText: "Yes",
      okType: "primary",
      cancelText: "No",
      onOk: async () => {
        setIsLoading(true);

        const dataSend = {
          contact: getMainContactName(data),
          accountName: getSiteName(data),
          account_id: data._id,
          owner: data.owner,
          origin: data.accountName ? "account" : "lead",
          contact_id: user?._id,
          contacID: data?.mainContact?._id,
        };

        const url = `${BACKEND_URL}/customers/requestSurvey`;
        const response = await axios.post(url, dataSend).catch((err) => {
          setIsLoading(false);
          console.log("error ", err);
          notification.error({
            message: "Survey request failed",
            description: "We couldn't submit your survey request. Please try again.",
          });
          return null;
        });

        if (response?.data) {
          setIsLoading(false);
          notification["success"]({ message: "Survey requested successfully" });
        }
      },
    });
  };

  const addDesactivated = true;

  // ========= EXPORT (all rows, not only current page) =========
  const exportColumns = [
    { title: "Site", field: "site" },
    { title: "Main Contact", field: "mainContact" },
    { title: "City", field: "city" },
    { title: "State", field: "state" },
    { title: "Open Quotes", field: "hasQuotes" },
    { title: "PCI", field: "pci" },
    { title: "PCI Status", field: "pciStatus" },
    { title: "PCI Expiration", field: "pciExpiration" },
    { title: "Contracted Plan", field: "contractedPlan" },
    { title: "PARC 360 Credits", field: "creditsBalance" },
    { title: "Open Invoices", field: "hasOpenInvoices" },
  ];

  const buildExportRows = (rows) =>
    (rows || []).map((r) => {
      const contractedPlan = hasRealPlan(r)
        ? r.PARCAgreementType.name
        : isParcEnrollmentPending(r)
        ? "Enrollment pending"
        : "Not enrolled";

      const creditsBalance =
        r.creditsPoolStatus === "enrolled"
          ? formatCents(r.creditsBalanceCents)
          : r.creditsPoolStatus === "unavailable"
          ? "Unavailable"
          : "Not enrolled";

      return {
        site: getSiteName(r),
        mainContact: getMainContactName(r),
        city: getCityName(r),
        state: getStateName(r),
        hasQuotes: yesNo(!!r?.hasQuotes),
        pci: getPciScore(r) === null ? "-" : getPciScore(r),
        pciStatus: r?.PCIStatus || "-",
        pciExpiration: r?.PCIValueExpirationDate
          ? moment(r.PCIValueExpirationDate).format("MM/DD/YYYY")
          : "-",
        contractedPlan,
        creditsBalance,
        hasOpenInvoices: yesNo(!!r?.hasOpenInvoices),
      };
    });

  if (user === null || user === undefined) return <Loader />;

  const tableData = withComputedFields(dataChart ? dataChart : openMySitesData);

  return (
    <div
      style={{ display: "flex", flexDirection: "column", paddingTop: "1rem" }}
    >
      {loadError ? (
        <Alert
          type="error"
          showIcon
          message="We couldn't load your sites."
          description="Please try again."
          action={
            <Button size="small" danger onClick={getData}>
              Retry
            </Button>
          }
          style={{ marginBottom: "1rem" }}
        />
      ) : null}
      {isLoading ? (
        <Loader />
      ) : (
        <div className="listContainer">
          <ThemeProvider theme={theme}>
            <MaterialTable
              columns={columns}
              className="genericTable"
              components={{
                Toolbar: (props) => (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      marginRight: "8px",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <MTableToolbar {...props} />
                    </div>
                    <Button onClick={() => setFiltering((f) => !f)}>
                      <FilterOutlined />
                    </Button>
                    {addDesactivated ? null : (
                      <Button
                        type="primary"
                        onClick={onCreateButtonClick}
                        style={{
                          marginLeft: "6px",
                          backgroundColor: "#00909E",
                          color: "#fff",
                        }}
                      >
                        <PlusOutlined />
                      </Button>
                    )}
                  </div>
                ),
              }}
              data={tableData}
              actions={
                dataChart
                  ? []
                  : [
                      (rd) => ({
                        icon: () => (
                          <VisibilityOutlinedIcon className="table-action-icon table-action-icon--view" />
                        ),
                        tooltip: "View",
                        onClick: (_e, rd) => onView(rd),
                      }),
                      (rd) => ({
                        icon: () => (
                          <FileUploadOutlinedIcon className="table-action-icon table-action-icon--upload" />
                        ),
                        tooltip: "Upload document",
                        onClick: (_e, rd) => openUploadDocument(rd),
                        hidden: !!rd?.companyName,
                      }),
                      (rd) => ({
                        icon: () => (
                          <RequestQuoteOutlinedIcon className="table-action-icon table-action-icon--warning" />
                        ),
                        tooltip: "Request quote",
                        onClick: (_e, rd) => requestSurvey(rd),
                        hidden:
                          (rd.PARCAgreement &&
                            rd.PARCAgreementEndDate &&
                            new Date(rd.PARCAgreementEndDate) < today) ||
                          rd.hasQuotes === true,
                      }),
                      (rd) => ({
                        icon: () => (
                          // Controlled (not trigger="click") on purpose: this icon
                          // sits inside MaterialTable's own IconButton, which is
                          // the only real, keyboard-reachable button in this slot.
                          // Letting Dropdown detect clicks on this nested SVG itself
                          // (a) requires nesting a second <button> to give it its
                          // own accessible target, which is invalid HTML and broke
                          // click handling entirely, and (b) still wouldn't open on
                          // keyboard Enter/Space, since a synthetic click from key
                          // activation targets the real outer button, not this
                          // child. Opening from the action's own onClick below
                          // fires for both mouse and keyboard on that real button.
                          <Dropdown
                            menu={{ items: getSiteOptionsMenuItems(rd) }}
                            trigger={[]}
                            open={openParcMenuId === rd._id}
                            onOpenChange={(open) =>
                              setOpenParcMenuId(open ? rd._id : null)
                            }
                          >
                            <MoreVertOutlinedIcon className="table-action-icon table-action-icon--options" />
                          </Dropdown>
                        ),
                        tooltip: "PARC & PCI options",
                        onClick: (_e, data) => {
                          setOpenParcMenuId((current) =>
                            current === data._id ? null : data._id
                          );
                          if (
                            hasRealPlan(data) &&
                            !selfInspectionLinks[data._id]
                          ) {
                            fetchSelfInspectionLink(data._id);
                          }
                        },
                        // Leads never have a PARC 360 account. Account rows
                        // always retain PCI Inspection so ineligible accounts
                        // receive the coverage message instead of a silent
                        // missing action.
                        hidden:
                          !!rd?.companyName,
                      }),
                    ]
              }
              options={{
                headerStyle: {
                  position: "sticky",
                  top: 0,
                  zIndex: 100,
                  backgroundColor: "white",
                },
                maxBodyHeight: `${windowSize.height - 250}px`,
                minBodyHeight: `${windowSize.height - 250}px`,
                actionsColumnIndex: -1,
                pageSize: pageSize || 20,
                filtering,
                exportMenu: [
                  {
                    label: "Export to PDF",
                    exportFunc: () => {
                      const rows = buildExportRows(tableData); // 🔥 todas las filas
                      ExportPdf(
                        exportColumns,
                        rows,
                        "Sites Report - " + new Date().toISOString()
                      );
                    },
                  },
                  {
                    label: "Export to CSV",
                    exportFunc: () => {
                      const rows = buildExportRows(tableData); // 🔥 todas las filas
                      ExportCsv(
                        exportColumns,
                        rows,
                        "Sites Report - " + new Date().toISOString()
                      );
                    },
                  },
                ],
              }}
              style={{ width: "100%" }}
              title={title ? title : "My Sites"}
              onCreateButtonClick={createAction}
            />
          </ThemeProvider>
        </div>
      )}
      <Modal
        title={modalTitle}
        open={isVisibleModal}
        setIsVisible={setIsVisibleModal}
        size={modalSize}
        className={"openModalDocumentsList"}
      >
        {modalContent}
      </Modal>
      <AccountDocumentUploadModal
        account={uploadAccount}
        open={!!uploadAccount}
        onClose={() => setUploadAccount(null)}
        onUploaded={getData}
      />
      <BuyCreditsModal
        account={buyCreditsAccount}
        open={!!buyCreditsAccount}
        onClose={() => setBuyCreditsAccount(null)}
        onRequested={getCreditPools}
      />
    </div>
  );
}
